'use client'
import { useState, useEffect, useRef } from 'react'
import { useAtom } from 'jotai'

import { useBroadcastChannel } from './useBroadcastChannel'
import {
    setSessionData,
    clearSessionData,
    SessionResponse,
    getSession,
    SessionSharingMessageType,
    broadcastSessionDataChange,
    broadcastClearSessionData,
    SESSION_SHARING_BROADCAST_CHANNEL,
    SESSION_SHARING_SENDER_ID,
} from '@docpace/shared-react-utils/sessionUtils'
import { ctxIsSessionInitialized } from '@docpace/shared-react-atoms'

export type SessionClearedOrigin = 'local' | 'broadcast'

interface UseSessionSharingProps {
    channelName?: string
    // app-level cleanup (apollo reset, redirect to signin, ...) run after the
    // session is cleared — 'local' when this tab called logout(), 'broadcast'
    // when another tab did
    onSessionCleared?: (origin: SessionClearedOrigin) => void | Promise<void>
}

export const useSessionSharing = ({
    channelName = SESSION_SHARING_BROADCAST_CHANNEL,
    onSessionCleared,
}: UseSessionSharingProps = {}) => {
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
    const [ isSessionInitialized, setIsSessionInitialized ] = useAtom(ctxIsSessionInitialized)
    // ref keeps the callback fresh inside the message handler, which is bound
    // once when the channel mounts
    const onSessionClearedRef = useRef(onSessionCleared)
    onSessionClearedRef.current = onSessionCleared
    const [sessionSharingChannel] = useBroadcastChannel({
        channelName,
        handleMessage: (data) => {
            return handleMessage(data)
        },
    })

    useEffect(() => {
        if (sessionSharingChannel) {
            sessionSharingChannel.onmessage = ({ data }) => handleMessage(data)
            try {
                /* istanbul ignore else: useEffect only runs in browser-like runtimes; jsdom always provides window. */
                if (typeof window !== 'undefined') {
                    window.setTimeout(() => {
                        sessionSharingChannel?.postMessage({
                            type: SessionSharingMessageType.AUTH_TOKEN_REQUEST,
                            senderId: SESSION_SHARING_SENDER_ID,
                        })
                    }, 200)
                }
            } catch (e) {
                console.log('Message could not be posted', e)
            }
        }
        /* istanbul ignore else: useEffect only runs in browser-like runtimes; jsdom always provides window. */
        if (typeof window !== 'undefined') {
            window.setTimeout(() => {
                setIsSessionInitialized(true)
            }, 1000)
        }
    }, [])

    const notifySessionUpdates = async (): Promise<void> => {
        try {
            const session = await getSession()
            if (session && sessionSharingChannel) {
                await broadcastSessionDataChange(sessionSharingChannel)
            }
        } catch (e) {
            console.error(e)
        }
    }

    const setSessionAndNotify = async (
        sessionData: SessionResponse
    ): Promise<void> => {
        await setSessionData(sessionData)
        await notifySessionUpdates()
        setLastUpdated(new Date())
    }

    const clearSession = async (): Promise<void> => {
        await clearSessionData()
        setLastUpdated(null)
    }

    // universal logout: clears this tab's session, tells every other tab on
    // the channel to do the same, then runs app-level cleanup
    const logout = async (): Promise<void> => {
        try {
            await broadcastClearSessionData(sessionSharingChannel ?? undefined)
        } catch (e) {
            console.log('Session sharing message was not posted', e)
        }
        await clearSessionData()
        setLastUpdated(null)
        await onSessionClearedRef.current?.('local')
    }

    const handleMessage = async (data: any) => {
        // ignore our own broadcasts — other hook instances in this tab share
        // the sender id, and the originating tab handles its own cleanup
        if (data?.senderId === SESSION_SHARING_SENDER_ID) return
        if (
            sessionSharingChannel &&
            data?.type === SessionSharingMessageType.AUTH_TOKEN_REQUEST
        ) {
            try {
                await broadcastSessionDataChange(sessionSharingChannel)
            } catch (e) {
                console.log('Session sharing message was not posted', e)
            }
        } else if (
            data?.type === SessionSharingMessageType.AUTH_TOKEN_RESPONSE
        ) {
            if (data?.session?.access_token) {
                await setSessionData(data.session)
                setLastUpdated(new Date())
            }
            setIsSessionInitialized(true)
        } else if (
            data?.type === SessionSharingMessageType.CLEAR_SESSION_DATA
        ) {
            await clearSessionData()
            setLastUpdated(null)
            await onSessionClearedRef.current?.('broadcast')
        }
    }

    return {
        clearSession,
        logout,
        setSessionAndNotify,
        isInitialized: isSessionInitialized,
        lastUpdated,
    }
}
